// @flow
import { unparse, parse } from 'papaparse';

import { type Char, type LINE_END, asString } from 'modules/string';

class CsvError extends Error {}

export const DEFAULT_CSV_QUOTE_CHARACTER: '"' = '"';

export type CellValue = string | number | boolean;

export type Row = $ReadOnlyArray<CellValue>;

export type FileSpecification = $ReadOnly<{|
  lineDelimiter?: LINE_END | null,
  fieldSeparator?: string | null,
  quoteCharacter?: Char | null,
  escapeCharacter?: string | null,
  header?: boolean,
  encoding?: string | null,
|}>;

export type ParseOptions = $ReadOnly<{|
  lineLimit?: number,
  fileSpecification?: FileSpecification,
  runInWorker?: boolean,
|}>;

export type ParsedCsv = $ReadOnly<{|
  rows: $ReadOnlyArray<Row>,
  errors: $ReadOnlyArray<CsvError>,
  specification: FileSpecification,
|}>;

export type PapaparseError = $ReadOnly<{|
  type: string, // A generalization of the error
  code: string, // Standardized error code
  message: string, // Human-readable details
  row: number, // Row index of parsed data where error is
|}>;

export type PapaparseMeta = $ReadOnly<{|
  delimiter: string, // Delimiter used
  linebreak: string, // Line break sequence used
  aborted: boolean, // Whether process was aborted
  fields?: ?$ReadOnlyArray<string>, // Array of field names
  truncated: boolean, // Whether preview consumed all input
|}>;

export type PaparseResults = $ReadOnly<{|
  data: $ReadOnlyArray<Row>,
  errors: $ReadOnlyArray<PapaparseError>,
  meta: PapaparseMeta,
|}>;

export type PapaparseParser = $ReadOnly<{|
  abort: () => void,
  pause: () => void,
  resume: () => void,
|}>;

export type PapaparseConfig = $ReadOnly<{|
  delimiter?: string | null,
  newline?: string | null,
  // if quoteChar is undefined, it'll default to ", if it's `null` it'll not quote at all
  quoteChar?: string | void | null,
  escapeChar?: string | null,
  header?: boolean,
  transformHeader?: (string, number) => string,
  dynamicTyping?: boolean,
  preview?: number,
  encoding?: null | string,
  worker?: boolean,
  comments?: boolean,
  step?: (PaparseResults, PapaparseParser) => void,
  complete?: (PaparseResults, Blob) => void,
  error?: (PapaparseError, Blob) => void,
  download?: boolean,
  downloadRequestHeaders?: { +[string]: string },
  downloadRequestBody?: string,
  skipEmptyLines?: boolean,
  chunk?: (PaparseResults, PapaparseParser) => void,
  chunkSize?: number,
  fastMode?: boolean,
  beforeFirstChunk?: (string) => string,
  withCredentials?: boolean,
  transform?: (string, string | number) => string,
  delimitersToGuess?: $ReadOnlyArray<string>,
|}>;

export type PapaparseParse = (Blob, PapaparseConfig) => void;

// eslint-disable-next-line babel/no-unused-expressions
(parse: PapaparseParse);

export const from2dArray = (array: $ReadOnlyArray<Row>): string =>
  unparse(array);

export const blobFrom2dArray = (array: $ReadOnlyArray<Row>): Blob =>
  new Blob([from2dArray(array)], {
    encoding: 'UTF-8',
    type: 'text/csv',
  });

export const papaparseConfigFromParseOptions = ({
  fileSpecification = { ...null },
  ...parseOptions
}: ParseOptions): PapaparseConfig => ({
  delimiter: fileSpecification.fieldSeparator
    ? fileSpecification.fieldSeparator
    : null,
  newline: fileSpecification.lineDelimiter,
  quoteChar: fileSpecification.quoteCharacter
    ? asString(fileSpecification.quoteCharacter)
    : undefined,
  escapeChar: fileSpecification.escapeCharacter,
  encoding: fileSpecification.encoding,
  header: false,
  preview: parseOptions.lineLimit,
  worker: parseOptions.runInWorker,
});

export const fileSpecificationFromPapaparseMeta = (
  meta: PapaparseMeta,
): FileSpecification => ({
  lineDelimiter: (meta.linebreak: any),
  fieldSeparator: meta.delimiter,
});

export const parsedCsvFromPapaparseResults = ({
  data,
  errors,
  meta,
}: PaparseResults): ParsedCsv => ({
  rows: data,
  errors: errors.map((e) => new CsvError(e.message)),
  specification: fileSpecificationFromPapaparseMeta(meta),
});

export const parsed = (file: Blob, opts?: ParseOptions): Promise<ParsedCsv> =>
  new Promise((resolve, reject) => {
    parse(file, {
      ...papaparseConfigFromParseOptions(opts || { ...null }),
      error: (e) => {
        reject(e);
      },
      complete: (results) => {
        resolve(parsedCsvFromPapaparseResults(results));
      },
    });
  });

export const cellValue =
  (file: ParsedCsv): ((row: number, column: number) => CellValue | null) =>
  (row: number, column: number): CellValue | null => {
    const rowData = file.rows[row];
    return (rowData && rowData[column]) || null;
  };

export const estimatedColumnCount = (file: ParsedCsv): number =>
  Math.max(
    ...Array(Math.min(file.rows.length) || 50)
      .fill(null)
      .map((_, idx) => file.rows[idx].length),
  );

export const rowCount = (file: ParsedCsv): number => file.rows.length;

export const columnByHeaderGuesses =
  (
    file: ParsedCsv,
  ): ((guesses: $ReadOnlyArray<string | RegExp>) => number | null) =>
  (guesses: $ReadOnlyArray<string | RegExp>): number | null =>
    guesses.reduce((acc, guess) => {
      if (typeof acc === 'number') return acc;

      const found = file.rows[0].findIndex((cell) =>
        Boolean(String(cell).trim().match(guess)),
      );

      return found === -1 ? null : found;
    }, null);
